<script setup lang="ts">
import { computed, nextTick, ref, useId, watch } from "vue";
import VButton from "./VButton.vue";
import VIcon from "./VIcon.vue";
import type { ICSSProperties } from "@/stores/types";
import { languageSwitchState } from "@/utils/pageLanguageNavigation";
import { languageControlLabel } from "@/utils/languageControlLabel";

const props = defineProps({
  items: {
    type: Array as () => Array<{ label: string; value: string }>,
    required: true,
  },
  selected: {
    type: String,
  },
  iconColor: {
    type: String,
    default: "white",
  },
  ariaLabel: String,
});

const emit = defineEmits(["change"]);

const showItemList = ref(false);
const menuId = useId();
const menu = ref<HTMLElement | null>(null);
let trigger: HTMLElement | undefined;
let restoreWhenReady = false;
const busy = computed(
  () => languageSwitchState.pending || languageSwitchState.initializing,
);
const controlLabel = computed(
  () => props.ariaLabel || languageControlLabel(props.selected),
);

const isRtl = computed(() =>
  (props.selected || "en").toLowerCase().startsWith("ar"),
);
const modalStyle = computed<ICSSProperties>(() => {
  if (isRtl.value) {
    return {
      top: "64px",
      left: "12px",
    };
  } else {
    return {
      top: "64px",
      right: "12px",
    };
  }
});

const tooltipArrowStyle = computed<ICSSProperties>(() => {
  if (isRtl.value) {
    return {
      left: "36px",
    };
  } else {
    return {
      right: "36px",
    };
  }
});

async function openItemList(event: Event) {
  if (busy.value) return;
  restoreWhenReady = false;
  if (event.currentTarget instanceof HTMLElement) trigger = event.currentTarget;
  showItemList.value = true;
  await nextTick();
  const option =
    menu.value?.querySelector<HTMLButtonElement>('[aria-checked="true"]') ||
    menu.value?.querySelector<HTMLButtonElement>("button");
  option?.focus();
}

function closeItemList(restoreFocus = false) {
  showItemList.value = false;
  if (restoreFocus && trigger?.isConnected) trigger.focus();
}

function selectLang(lang: string) {
  if (languageSwitchState.pending || languageSwitchState.initializing) return;
  closeItemList(true);
  emit("change", lang);
  restoreWhenReady = lang !== props.selected || busy.value;
}

function navigateChoices(event: KeyboardEvent) {
  if (event.key === "Escape") {
    event.preventDefault();
    closeItemList(true);
    return;
  }
  if (event.key === "Tab") {
    closeItemList(true);
    return;
  }
  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
  event.preventDefault();
  const choices = Array.from(
    menu.value?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") ||
      [],
  );
  if (!choices.length) return;
  const current = choices.findIndex(
    (choice) => choice === document.activeElement,
  );
  const index =
    event.key === "Home"
      ? 0
      : event.key === "End"
        ? choices.length - 1
        : (current + (event.key === "ArrowDown" ? 1 : -1) + choices.length) %
          choices.length;
  choices[index].focus();
}

watch(busy, async (pending) => {
  if (!pending && restoreWhenReady) {
    restoreWhenReady = false;
    await nextTick();
    if (trigger?.isConnected) trigger.focus();
  }
});
</script>

<template>
  <VButton
    :disabled="busy"
    :aria-busy="languageSwitchState.pending"
    :aria-label="controlLabel"
    aria-haspopup="menu"
    :aria-expanded="showItemList"
    :aria-controls="menuId"
    text-color="#FFFFFF"
    color="transparent"
    color-pressed="transparent"
    :dimension="{
      margin: '4px 0',
      padding: '0',
    }"
    @click="openItemList"
    @keydown.down.prevent="openItemList"
  >
    <VIcon
      v-bind="{
        icon: 'globe',
        color: props.iconColor,
      }"
      :style="{ margin: 'auto', color: props.iconColor }"
    ></VIcon>
  </VButton>
  <Teleport to="body">
    <transition name="scale-in">
      <div
        class="tooltip-modal"
        @click.self="closeItemList()"
        v-if="showItemList"
      >
        <div :style="modalStyle" class="tooltip">
          <div :style="tooltipArrowStyle" class="tooltip-arrow"></div>
          <div
            :id="menuId"
            ref="menu"
            role="menu"
            :aria-label="controlLabel"
            class="flex flex-col language-options"
            @keydown="navigateChoices"
          >
            <div v-for="item in props.items" :key="item.value">
              <div class="flex flex-col items-center">
                <div
                  class="flex flex-col"
                  :class="{
                    'selected-item': item.value === props.selected,
                  }"
                >
                  <VButton
                    role="menuitemradio"
                    :aria-checked="item.value === props.selected"
                    :disabled="busy"
                    width="160px"
                    :hollow="true"
                    text-color="black"
                    @click="selectLang(item.value)"
                  >
                    {{ item.label }}
                  </VButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.scale-in-enter,
.scale-in-enter-from {
  transform: scale(0.88);
  opacity: 0;
}
.scale-in-leave-to {
  transform: scale(0.88);
  opacity: 0;
}

.scale-in-enter-active,
.scale-in-leave-active {
  transition:
    transform 0.3s,
    opacity 0.5s;
}

.tooltip {
  position: absolute;
  top: 40px;
  padding: 4px;
  min-width: 84px;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0px 11px 15px rgba(0, 0, 0, 0.15);
  z-index: 20;
  display: block;
  width: max-content;
}

.tooltip-arrow {
  content: "";
  position: absolute;
  top: 0;
  width: 0;
  height: 0;
  border: 6px solid transparent;
  border-bottom-color: #fff;
  border-top: 0;
  margin-left: -6px;
  margin-top: -6px;
}

.language-options {
  max-height: calc(100dvh - 88px);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.tooltip-modal {
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 20;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: none;
  max-width: 425px;
  margin: auto;
}

.selected-item {
  background-color: rgba(200, 200, 200, 0.4);
}
</style>
